import { Flex, Text } from "styles/utility-components";

import Button from "components/ui/button/Button";
import Modal from "components/ui/modal/Modal";
import toast from "components/ui/toast/Toast";
import { CustomerDocumentDetails } from "lib/hooks/api/customer_documents/useCustomerDocuments";
import { useDeleteCustomerDocuments } from "lib/hooks/api/customer_documents/useDeleteCustomerDocuments";
import { pluralise } from "lib/utils";
import { DeleteDocumentType } from "../../../lib/types/graphQLEnums";

type Props = {
  selectedDocuments: CustomerDocumentDetails[];
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onSuccess: () => void;
  documentType: DeleteDocumentType;
};
export function DeleteCustomerDocumentModal({
  selectedDocuments,
  isOpen,
  onOpenChange,
  onSuccess,
  documentType,
}: Props) {
  const { mutate: deleteDocumentsMutation, isLoading } = useDeleteCustomerDocuments({
    onSuccess: async (data) => {
      const deletedDocuments = data.deleteCustomerDocuments;

      if (deletedDocuments.length === 1) {
        toast.success(`Deleted document ${deletedDocuments[0].fileName}`);
      } else {
        const numberOfFolders = deletedDocuments.filter((doc) => doc.isFolder).length;
        const numberOfFiles = deletedDocuments.length - numberOfFolders;
        const folderText = numberOfFolders > 0 ? pluralise(numberOfFolders, "folder") : "";
        const fileText = numberOfFiles > 0 ? pluralise(numberOfFiles, "file") : "";

        let message = "";
        if (folderText && fileText) {
          message = `Deleted ${folderText} and ${fileText}`;
        } else if (folderText || fileText) {
          message = `Deleted ${folderText || fileText}`;
        }

        toast.success(message);
      }

      onOpenChange(false); // Close modal on success

      // Remove deleted documents from selection, if they were selected
      onSuccess();
    },
    onError: (_err) => toast.error(`Failed to delete documents`),
  });

  const deleteDocuments = () => {
    const ids = [];
    const uiPaths = [];

    for (const doc of selectedDocuments) {
      // Add ids for both files & folders
      ids.push(doc.id);
      if (doc.isFolder) {
        // The ui path to delete when you're deleting a folder is
        // the folder's ui path + file name
        const prefixPath = doc.uiPath ? `${doc.uiPath}/` : "";
        const fullUiPath = prefixPath + doc.fileName;
        uiPaths.push(fullUiPath);
      }
    }

    deleteDocumentsMutation({
      request: { ids, uiPaths, documentType },
    });
  };

  return (
    <Modal
      open={isOpen}
      showCloseButton={false}
      onOpenChange={onOpenChange}
      padding={32}
      maxWidth="440px"
    >
      <Flex flexDirection="column" gap={16} aria-label="Delete modal">
        <Flex flexDirection="column" gap={8}>
          <Text bold>About to delete content</Text>
          <Text>Are you sure you want to delete this content? This action cannot be undone.</Text>
        </Flex>
        <Flex gap={8} alignSelf="flex-end">
          <Button buttonType="secondary" disabled={isLoading} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button buttonStyle="destructive" loading={isLoading} onClick={deleteDocuments}>
            Delete
          </Button>
        </Flex>
      </Flex>
    </Modal>
  );
}
